import { ContentCard } from "@msuite/ui/content-card";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { DataTable } from "@msuite/ui/table";
import type { RoleDetail } from "@msuite/utils/types";
import { useMemo } from "react";
import { canManageMembers } from "@/components/citadel";
import { createMemberColumns } from "./members-table-config";
import { RoleMemberSelect } from "./role-member-select";

/** Props Interface */
interface RoleMembersProps {
	roleId: string;
	role: RoleDetail;
}

/**
 * The role's members tab: everyone who carries the role, with when and by whom each was added,
 * and the remove action per row. A table, because its built-in search is what keeps the view
 * usable once a role has more bearers than fit on a screen. The add control sits beside the
 * search.
 *
 * The `member` system role renders a sentence instead — every non-owner carries it by standing,
 * and the server refuses its membership writes — mirroring the grid the `admin` role's
 * permissions tab replaces with its own sentence.
 */
export function RoleMembers({ roleId, role }: RoleMembersProps) {
	/** Context */
	const { t, language } = useTranslation();

	/** Computed */
	// `language` is a dependency although the callback does not read it: the column headers are
	// rendered through `localized(...)` at build time, so a language switch has to rebuild them.
	const columns = useMemo(() => createMemberColumns(roleId), [roleId, language]);

	/** Guard */
	if (!canManageMembers(role)) {
		return (
			<ContentCard
				title={t("organization.members")}
				icon={icon("Lock")}
			>
				<p
					className="label"
					data-cy="role-members-locked"
				>
					{t("roles.members_locked")}
				</p>
			</ContentCard>
		);
	}

	/** Render */
	return (
		<DataTable
			data={role.members}
			columns={columns}
			persistenceKey="role-members-table"
			// Nothing here loads on its own: the members arrive with the role the page already
			// holds, and every write invalidates that query rather than refetching the table.
			isLoading={false}
			refetch={undefined}
			queryKey={undefined}
			menu={null}
			slots={{
				"right-of-search": () => (
					<RoleMemberSelect
						roleId={roleId}
						members={role.members}
					/>
				),
			}}
		/>
	);
}
